import { Injectable } from '@angular/core';
import {
  HttpClient,
  HttpErrorResponse,
  HttpHeaders,
  HttpParams,
} from '@angular/common/http';
import { firstValueFrom } from 'rxjs';
import { LoginResult, TokenResponse } from '../models/auth.models';
import { ConfigService } from './config.service';

@Injectable({
  providedIn: 'root',
})
export class KeycloakTokenService {
  constructor(
    private readonly http: HttpClient,
    private readonly configService: ConfigService,
  ) {}

  login(username: string, password: string): Promise<LoginResult> {
    return this.requestToken(
      new HttpParams()
        .set('client_id', this.configService.keycloak.clientId)
        .set('username', username)
        .set('password', password)
        .set('grant_type', 'password'),
    );
  }

  refreshToken(refreshToken: string): Promise<TokenResponse | null> {
    return this.requestToken(
      new HttpParams()
        .set('client_id', this.configService.keycloak.clientId)
        .set('refresh_token', refreshToken)
        .set('grant_type', 'refresh_token'),
    ).then((result) =>
      result.status === 'SUCCESS' ? (result.tokens ?? null) : null,
    );
  }

  private requestToken(body: HttpParams): Promise<LoginResult> {
    const tokenUrl = this.configService.resolveKeycloakTokenUrl();

    return firstValueFrom(
      this.http.post<TokenResponse>(tokenUrl, body.toString(), {
        headers: this.formHeaders(),
      }),
    )
      .then((tokens) => ({ status: 'SUCCESS' as const, tokens }))
      .catch((error: HttpErrorResponse) => {
        if (this.configService.enableApiLogs) {
          console.error('[Auth] Token request failed', {
            status: error.status,
            url: tokenUrl,
            message: error.message,
            body: error.error,
          });
        }

        const oauthError = error.error?.error as string | undefined;
        const oauthDescription = error.error?.error_description as
          | string
          | undefined;

        if (
          oauthError === 'invalid_grant' ||
          error.status === 401 ||
          oauthDescription?.toLowerCase().includes('invalid user credentials')
        ) {
          return { status: 'UNAUTHORIZED' as const };
        }

        if (error.status === 400) {
          return { status: 'DISABLED_ACCOUNT' as const };
        }

        if (error.status === 0 || error.status === 504) {
          return { status: 'TIMEOUT' as const };
        }

        return { status: 'ERROR' as const };
      });
  }

  private formHeaders(): HttpHeaders {
    return new HttpHeaders({
      'Content-Type': 'application/x-www-form-urlencoded',
    });
  }
}
